import {
  UploadStudentImportFile,
  PreviewStudentImportFile,
  saveStudentImportMapping,
  getStudentImportMappings,
  getStudentImportMapping,
  startStudentImport,
  getStudentImportStatus,
  getSessionTypes,
} from "../../DriveScout/API/models/ImportWizard";

const state = {
  columnMapping: {},
  currentPage: 1,
  rowsPerPage: 5,
  currentStep: 1,
  fileUploaded: false,
  fileDirectory: "",
  fileName: "",
  csvHeaders: [],
  previewData: [],
  availableFields: [],
  importOptions: {
    importPickupLocations: true,
    test: false,
    btwSessionTypeId: null,
    roadTestSessionTypeId: null,
  },
  importing: false,
  importResults: null,
  file: null,
  isDragging: false,
  loading: false,
  error: null,
  requiredFields: [],
  sessionTypes: [],
  // Async import progress tracking
  importId: null,
  importStatus: null, // pending, processing, completed, failed
  importProgress: {
    total: 0,
    processed: 0,
    percentage: 0,
  },
  importFailureReason: null,
};

const mutations = {
  SET_STUDENT_IMPORT_COLUMN_MAPPING(state, mapping) {
    state.columnMapping = mapping;
  },
  SET_STUDENT_IMPORT_PAGE(state, page) {
    state.currentPage = page;
  },
  SET_STUDENT_IMPORT_STEP(state, step) {
    state.currentStep = step;
  },
  SET_STUDENT_IMPORT_FILE_UPLOADED(state, uploaded) {
    state.fileUploaded = uploaded;
  },
  SET_STUDENT_IMPORT_FILE_INFO(state, { directory, name }) {
    state.fileDirectory = directory;
    state.fileName = name;
  },
  SET_STUDENT_IMPORT_PREVIEW_DATA(state, { headers, data, fields }) {
    state.csvHeaders = headers;
    state.previewData = data;
    state.availableFields = fields;
  },
  SET_STUDENT_IMPORT_OPTIONS(state, options) {
    state.importOptions = { ...options };
  },
  SET_STUDENT_IMPORT_IMPORTING(state, importing) {
    state.importing = importing;
  },
  SET_STUDENT_IMPORT_RESULTS(state, results) {
    state.importResults = results;
  },
  RESET_STUDENT_IMPORT(state) {
    Object.assign(state, {
      columnMapping: {},
      currentPage: 1,
      rowsPerPage: 5,
      currentStep: 1,
      fileUploaded: false,
      fileDirectory: "",
      fileName: "",
      csvHeaders: [],
      previewData: [],
      availableFields: [],
      importOptions: {
        importPickupLocations: true,
        test: false,
        btwSessionTypeId: null,
        roadTestSessionTypeId: null,
      },
      importing: false,
      importResults: null,
      file: null,
      isDragging: false,
      loading: false,
      error: null,
      requiredFields: [],
      sessionTypes: [],
      importId: null,
      importStatus: null,
      importProgress: {
        total: 0,
        processed: 0,
        percentage: 0,
      },
      importFailureReason: null,
    });
  },
  SET_STUDENT_IMPORT_FILE(state, file) {
    state.file = file;
  },
  SET_STUDENT_IMPORT_DRAGGING(state, isDragging) {
    state.isDragging = isDragging;
  },
  SET_STUDENT_IMPORT_LOADING(state, loading) {
    state.loading = loading;
  },
  SET_STUDENT_IMPORT_ERROR(state, error) {
    state.error = error;
  },
  SET_STUDENT_IMPORT_REQUIRED_FIELDS(state, fields) {
    state.requiredFields = fields;
  },
  SET_STUDENT_IMPORT_SESSION_TYPES(state, sessionTypes) {
    state.sessionTypes = sessionTypes;
  },
  SET_STUDENT_IMPORT_ID(state, importId) {
    state.importId = importId;
  },
  SET_STUDENT_IMPORT_STATUS(state, status) {
    state.importStatus = status;
  },
  SET_STUDENT_IMPORT_PROGRESS(state, progress) {
    state.importProgress = { ...progress };
  },
  SET_STUDENT_IMPORT_FAILURE_REASON(state, reason) {
    state.importFailureReason = reason;
  },
};

const actions = {
  PREVIEW_STUDENT_IMPORT_FILE({ commit }, data) {
    return PreviewStudentImportFile(data.school_id, data.file_id).then(
      (response) => {
        return response;
      },
    );
  },
  UPLOAD_STUDENT_IMPORT_FILE({ commit }, data) {
    return UploadStudentImportFile(data.school_id, data.formData).then(
      (response) => {
        return response;
      },
    );
  },
  SAVE_STUDENT_IMPORT_MAPPING({ commit }, data) {
    return saveStudentImportMapping(
      data.school_id,
      data.name,
      data.mapping,
    ).then((response) => {
      return response;
    });
  },
  GET_STUDENT_IMPORT_MAPPINGS({ commit }, data) {
    return getStudentImportMappings(data.school_id).then((response) => {
      return response;
    });
  },
  GET_STUDENT_IMPORT_MAPPING({ commit }, data) {
    return getStudentImportMapping(data.school_id, data.mapping_id).then(
      (response) => {
        return response;
      },
    );
  },
  START_STUDENT_IMPORT({ commit }, data) {
    return startStudentImport(
      data.school_id,
      data.file_id,
      data.test,
      data.column_mapping,
      data.import_pickup_locations,
      data.btw_session_type_id,
      data.road_test_session_type_id,
    ).then((response) => {
      return response;
    });
  },
  GET_STUDENT_IMPORT_STATUS({ commit }, data) {
    return getStudentImportStatus(data.school_id, data.import_id).then(
      (response) => {
        const statusData = response.data;
        commit("SET_STUDENT_IMPORT_STATUS", statusData.status);
        commit("SET_STUDENT_IMPORT_PROGRESS", statusData.progress);
        if (statusData.failure_reason) {
          commit(
            "SET_STUDENT_IMPORT_FAILURE_REASON",
            statusData.failure_reason,
          );
        }
        if (statusData.results) {
          commit("SET_STUDENT_IMPORT_RESULTS", statusData.results);
        }
        return response;
      },
    );
  },
  FETCH_STUDENT_IMPORT_SESSION_TYPES({ commit }, data) {
    return getSessionTypes(data.school_id).then((response) => {
      commit("SET_STUDENT_IMPORT_SESSION_TYPES", response.data.sessionTypes);
      return response;
    });
  },
  SET_STUDENT_IMPORT_COLUMN_MAPPING({ commit }, mapping) {
    commit("SET_STUDENT_IMPORT_COLUMN_MAPPING", mapping);
  },
  SET_STUDENT_IMPORT_PAGE({ commit }, page) {
    commit("SET_STUDENT_IMPORT_PAGE", page);
  },
  SET_STUDENT_IMPORT_STEP({ commit }, step) {
    commit("SET_STUDENT_IMPORT_STEP", step);
  },
  SET_STUDENT_IMPORT_FILE_UPLOADED({ commit }, uploaded) {
    commit("SET_STUDENT_IMPORT_FILE_UPLOADED", uploaded);
  },
  SET_STUDENT_IMPORT_FILE_INFO({ commit }, info) {
    commit("SET_STUDENT_IMPORT_FILE_INFO", info);
  },
  SET_STUDENT_IMPORT_PREVIEW_DATA({ commit }, data) {
    commit("SET_STUDENT_IMPORT_PREVIEW_DATA", data);
  },
  SET_STUDENT_IMPORT_OPTIONS({ commit }, options) {
    commit("SET_STUDENT_IMPORT_OPTIONS", options);
  },
  SET_STUDENT_IMPORT_IMPORTING({ commit }, importing) {
    commit("SET_STUDENT_IMPORT_IMPORTING", importing);
  },
  SET_STUDENT_IMPORT_RESULTS({ commit }, results) {
    commit("SET_STUDENT_IMPORT_RESULTS", results);
  },
  RESET_STUDENT_IMPORT({ commit }) {
    commit("RESET_STUDENT_IMPORT");
  },
  SET_STUDENT_IMPORT_FILE({ commit }, file) {
    commit("SET_STUDENT_IMPORT_FILE", file);
  },
  SET_STUDENT_IMPORT_DRAGGING({ commit }, isDragging) {
    commit("SET_STUDENT_IMPORT_DRAGGING", isDragging);
  },
  SET_STUDENT_IMPORT_LOADING({ commit }, loading) {
    commit("SET_STUDENT_IMPORT_LOADING", loading);
  },
  SET_STUDENT_IMPORT_ERROR({ commit }, error) {
    commit("SET_STUDENT_IMPORT_ERROR", error);
  },
  SET_STUDENT_IMPORT_REQUIRED_FIELDS({ commit }, fields) {
    commit("SET_STUDENT_IMPORT_REQUIRED_FIELDS", fields);
  },
};

const getters = {
  studentImportColumnMapping: (state) => state.columnMapping,
  studentImportCurrentPage: (state) => state.currentPage,
  studentImportRowsPerPage: (state) => state.rowsPerPage,
  studentImportCurrentStep: (state) => state.currentStep,
  studentImportFileUploaded: (state) => state.fileUploaded,
  studentImportFileInfo: (state) => ({
    directory: state.fileDirectory,
    name: state.fileName,
  }),
  studentImportPreviewData: (state) => ({
    headers: state.csvHeaders,
    data: state.previewData,
    fields: state.availableFields,
  }),
  studentImportOptions: (state) => state.importOptions,
  studentImportImporting: (state) => state.importing,
  studentImportResults: (state) => state.importResults,
  studentImportFile: (state) => state.file,
  studentImportDragging: (state) => state.isDragging,
  studentImportLoading: (state) => state.loading,
  studentImportError: (state) => state.error,
  studentImportRequiredFields: (state) => state.requiredFields,
  studentImportMappedPreviewData: (state) => {
    if (!state.previewData.length) {
      return [];
    }

    return state.previewData.map((row) => {
      const mappedRow = {};
      Object.keys(state.columnMapping).forEach((csvCol) => {
        const systemField = state.columnMapping[csvCol];
        if (systemField) {
          mappedRow[systemField] = row[csvCol];
        }
      });
      return mappedRow;
    });
  },
  validateStudentImportMapping: (state) => {
    const mappedFields = Object.values(state.columnMapping);
    return state.requiredFields.every((field) => mappedFields.includes(field));
  },
  studentImportSessionTypes: (state) => state.sessionTypes,
  studentImportId: (state) => state.importId,
  studentImportStatus: (state) => state.importStatus,
  studentImportProgress: (state) => state.importProgress,
  studentImportFailureReason: (state) => state.importFailureReason,
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
};
